/**
 * AudioSlice — owns the AudioContext, scheduled TTS chunks and playhead.
 * Invariants: AudioContext is constructed ONLY inside ensureAudioCtx() and ONLY when a
 * fresh UserGestureToken is provided (autoplay policy compliance).
 */
import type { StateCreator } from "zustand";
import {
  AudioGestureRequiredError,
  isValidUserGestureToken,
  type ScheduledChunk,
  type UserGestureToken,
} from "../../domain/types";
import type { DebateStore } from "../debateStore";

type WindowWithWebkit = typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

const TTS_SAMPLE_RATE = 48_000;

function getAudioContextCtor(): typeof AudioContext {
  const w = globalThis as WindowWithWebkit;
  if (typeof AudioContext !== "undefined") return AudioContext;
  if (w.webkitAudioContext) return w.webkitAudioContext;
  throw new Error("AudioContext is not supported in this environment");
}

export interface AudioSlice {
  audioCtx: AudioContext | null;
  ttsAnalyser: AnalyserNode | null;
  scheduledChunks: Record<string, ScheduledChunk>;
  playheadMs: number;
  muted: boolean;
  /**
   * Speaker whose TTS chunk is CURRENTLY audible.
   *
   * The TTS engine queues turns as soon as their `endedAt` is set, but
   * playback can lag the LLM by tens of seconds. The Roundtable spotlight
   * MUST follow what the user is hearing, not what the LLM is generating
   * — otherwise the avatar that's "lit" desyncs from the voice. Set this
   * pair when audio.play() resolves, clear it on `ended`/error.
   */
  audibleSpeakerId: string | null;
  audibleTurnId: string | null;

  ensureAudioCtx(gesture: UserGestureToken | null): AudioContext;
  setTtsAnalyser(analyser: AnalyserNode): void;
  enqueueChunk(chunk: ScheduledChunk): void;
  advancePlayhead(ms: number): void;
  toggleMute(): void;
  setAudibleSpeaker(speakerId: string, turnId: string): void;
  clearAudibleSpeaker(turnId: string): void;
  suspendForBackground(): Promise<void>;
  resumeForForeground(): Promise<void>;
  resetAudio(): void;
}

export type AudioSliceCreator = StateCreator<
  DebateStore,
  [["zustand/immer", never]],
  [],
  AudioSlice
>;

export const createAudioSlice: AudioSliceCreator = (set, get) => ({
  audioCtx: null,
  ttsAnalyser: null,
  scheduledChunks: {},
  playheadMs: 0,
  muted: false,
  audibleSpeakerId: null,
  audibleTurnId: null,

  ensureAudioCtx: (gesture) => {
    const existing = get().audioCtx;
    if (existing) return existing;
    if (!isValidUserGestureToken(gesture)) {
      throw new AudioGestureRequiredError(
        "ensureAudioCtx requires a fresh UserGestureToken when no AudioContext exists.",
      );
    }
    const Ctor = getAudioContextCtor();
    // Synchronous construction inside the gesture frame is the part that matters
    // for browser autoplay policy. resume() is best-effort and fires async.
    const ctx = new Ctor({ latencyHint: "interactive", sampleRate: TTS_SAMPLE_RATE });
    if (ctx.state === "suspended") {
      void ctx.resume().catch(() => {
        /* swallow — caller will observe via state */
      });
    }
    set((state) => {
      state.audioCtx = ctx;
    });
    return ctx;
  },

  setTtsAnalyser: (analyser) => {
    set((state) => {
      state.ttsAnalyser = analyser as unknown as AnalyserNode;
    });
  },

  enqueueChunk: (chunk) => {
    set((state) => {
      state.scheduledChunks[chunk.id] = chunk;
    });
  },

  advancePlayhead: (ms) => {
    set((state) => {
      if (ms > state.playheadMs) {
        state.playheadMs = ms;
      }
    });
  },

  toggleMute: () => {
    set((state) => {
      state.muted = !state.muted;
      // Mute drops the auditory channel — release the spotlight binding
      // so the visual layer falls back to LLM-token-driven speaker.
      if (state.muted) {
        state.audibleSpeakerId = null;
        state.audibleTurnId = null;
      }
    });
  },

  setAudibleSpeaker: (speakerId, turnId) => {
    set((state) => {
      state.audibleSpeakerId = speakerId;
      state.audibleTurnId = turnId;
    });
  },

  clearAudibleSpeaker: (turnId) => {
    set((state) => {
      // Only clear if the caller is the same turn — defends against
      // a stale "ended" event firing after a new turn already began
      // playing (rare race; cheap guard).
      if (state.audibleTurnId === turnId) {
        state.audibleSpeakerId = null;
        state.audibleTurnId = null;
      }
    });
  },

  suspendForBackground: async () => {
    const ctx = get().audioCtx;
    if (!ctx) return;
    if (ctx.state === "running") {
      await ctx.suspend();
    }
  },

  resumeForForeground: async () => {
    const ctx = get().audioCtx;
    if (!ctx) return;
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
  },

  resetAudio: () => {
    set((state) => {
      state.scheduledChunks = {};
      state.playheadMs = 0;
      state.audibleSpeakerId = null;
      state.audibleTurnId = null;
    });
  },
});
